/**
 * Author: Cascade using Claude Sonnet 4.5
 * Date: 2025-11-26
 * PURPOSE: Poetiq Community Solver landing page - Merged with Explainer for comprehensive audit view.
 *          Includes detailed breakdown of Poetiq's "Meta-System" and Pareto claims.
 *
 * SRP/DRY check: Pass - Single page for community progress and methodology explanation
 */

import React, { useEffect, useMemo, useState } from 'react';
import { useLocation } from 'wouter';
import {
  ExternalLink,
  RefreshCw,
  Play,
  CheckCircle,
  Target,
  Code,
  AlertCircle,
  Brain,
  TestTube,
  TrendingUp,
  Cpu,
  Search
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { PuzzleProgressGrid } from '@/components/poetiq/PuzzleProgressGrid';
import { usePoetiqCommunityProgress } from '@/hooks/usePoetiqCommunityProgress';
import { usePoetiqModels } from '@/hooks/usePoetiqModels';
import { useRequiresUserApiKey } from '@/hooks/useAppConfig';

const EXPERT_OPTIONS = [
  { value: '1', label: '1 Expert (Poetiq-a)' },
  { value: '2', label: '2 Experts (Poetiq-b)' },
  { value: '8', label: '8 Experts (Poetiq-c)' },
];

const MODEL_CARD_COLORS = [
  'bg-blue-50 border-blue-200 text-blue-800',
  'bg-gray-100 border-gray-200 text-gray-800',
  'bg-amber-50 border-amber-200 text-amber-800',
];

const ROUTING_LABELS: Record<string, string> = {
  direct: 'Direct API',
  openrouter: 'OpenRouter Relay',
};

const mapProviderToSlug = (provider?: string): 'openrouter' | 'gemini' | 'openai' => {
  if (!provider) return 'openrouter';
  const normalized = provider.toLowerCase();
  if (normalized.includes('google')) return 'gemini';
  if (normalized.includes('openai')) return 'openai';
  if (normalized.includes('openrouter')) return 'openrouter';
  return 'openrouter';
};

const getRoutingLabel = (routing?: string) => {
  if (!routing) return ROUTING_LABELS.openrouter;
  return ROUTING_LABELS[routing] ?? ROUTING_LABELS.openrouter;
};

const KEY_PLACEHOLDERS: Record<'openrouter' | 'gemini' | 'openai', string> = {
  openrouter: 'sk-or-...',
  gemini: 'AIza...',
  openai: 'sk-...',
};

const PROVIDER_KEY_URLS: Record<'openrouter' | 'gemini' | 'openai', string> = {
  openrouter: 'https://openrouter.ai/keys',
  gemini: 'https://aistudio.google.com/app/apikey',
  openai: 'https://platform.openai.com/account/api-keys',
};

export default function PoetiqCommunity() {
  const [, navigate] = useLocation();
  const progress = usePoetiqCommunityProgress();
  const { data: models = [], isLoading: modelsLoading } = usePoetiqModels();
  const [showSettings, setShowSettings] = useState(false);

  // Configuration state
  const [selectedModelId, setSelectedModelId] = useState('');
  const [apiKey, setApiKey] = useState('');
  const [numExperts, setNumExperts] = useState('2');

  useEffect(() => {
    document.title = 'Poetiq Integration Audit';
  }, []);

  useEffect(() => {
    if (!selectedModelId && models.length > 0) {
      const defaultModel = models.find(m => m.recommended) ?? models[0];
      setSelectedModelId(defaultModel.id);
    }
  }, [models, selectedModelId]);

  const selectedModel = models.find(m => m.id === selectedModelId) ?? null;
  const providerSlug = mapProviderToSlug(selectedModel?.provider);
  const globalByokRequired = useRequiresUserApiKey();
  const modelRequiresByo = selectedModel?.requiresBYO ?? false;
  const requiresApiKey = globalByokRequired || modelRequiresByo;
  const routingLabel = getRoutingLabel(selectedModel?.routing);
  const recommendedModels = useMemo(() => models.filter(m => m.recommended), [models]);
  const keyPlaceholder = KEY_PLACEHOLDERS[providerSlug];
  const providerKeyUrl = PROVIDER_KEY_URLS[providerSlug];

  const nextPuzzle = progress.getNextRecommended();
  const canStart = !!nextPuzzle && !!selectedModel && !modelsLoading && (!requiresApiKey || !!apiKey.trim());
  const usingProjectKey = !apiKey.trim();

  const handleOpenTransparency = () => {
    if (!nextPuzzle) return;
    navigate(`/puzzle/poetiq/${nextPuzzle}`);
  };

  const handleRunNext = () => {
    if (!nextPuzzle || !selectedModel) return;

    sessionStorage.setItem('poetiq_config', JSON.stringify({
      apiKey,
      provider: providerSlug,
      model: selectedModel.id,
      numExperts: parseInt(numExperts, 10),
      temperature: 1.0,
      autoStart: true,
      routing: selectedModel.routing,
    }));

    navigate(`/puzzle/poetiq/${nextPuzzle}`);
  };

  return (
    <div className="min-h-screen bg-gray-50 font-sans">
      <div className="max-w-7xl mx-auto p-4 space-y-6">

        {/* Header Section */}
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold flex items-center gap-2 text-gray-900">
                <Search className="h-6 w-6 text-indigo-600" />
                Independent Audit of Poetiq's SOTA Results
              </h1>
              <p className="text-base text-gray-600">
                Community-driven verification of claimed performance on ARC-AGI-1 & 2 benchmarks.
              </p>
            </div>
            <div className="flex gap-2">
              <Button variant="outline" asChild>
                <a href="https://poetiq.ai/posts/arcagi_announcement/" target="_blank" rel="noreferrer">
                  <ExternalLink className="h-4 w-4 mr-2" />
                  Read Blog Post
                </a>
              </Button>
              <Button
                onClick={() => setShowSettings(!showSettings)}
                variant="default"
                className="bg-indigo-600 hover:bg-indigo-700"
              >
                {showSettings ? 'Hide Settings' : 'Start Solver'}
              </Button>
            </div>
          </div>
        </div>
        <Card className="bg-blue-50 border border-blue-200">
          <CardHeader className="pb-3">
            <CardTitle className="text-lg flex items-center gap-2 text-blue-900">
              <Brain className="h-5 w-5 text-blue-700" />
              How Poetiq decides which answer to keep
            </CardTitle>
            <CardDescription className="text-blue-800">
              Every run is a mini tournament of AI coders. Here’s the plain-language checklist that happens behind the scenes.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-2 text-sm text-blue-900">
            <p>1. Several “experts” each write their own Python rule and try it on the training grids.</p>
            <p>
              2. We keep score: how many examples each expert passed, how many times they revised their idea, and what the
              hidden-test answer looks like.
            </p>
            <p>
              3. If multiple experts agree on the same hidden-test answer, that answer gets extra weight—it’s basically a
              group vote.
            </p>
            <p>
              4. The winning code plus a mini history for every expert is saved into the explanation database, so nothing
              disappears after the run.
            </p>
          </CardContent>
        </Card>
        <div className="bg-indigo-50 border border-indigo-200 rounded-lg p-3 text-sm text-indigo-900 flex flex-col gap-2 md:flex-row md:items-center md:justify-between">
          <p>
            <strong>New transparency upgrade:</strong> the Poetiq Solver page now shows live phase timers, expert cards,
            and token spend as the backend runs. You can watch every prompt, iteration, and Python execution in real time.
          </p>
          <Button
            variant="secondary"
            disabled={!nextPuzzle}
            onClick={handleOpenTransparency}
            className="md:w-auto w-full bg-indigo-600/90 hover:bg-indigo-600 text-white"
          >
            View Transparency Dashboard
          </Button>
        </div>

        {/* Collapsible Settings */}
        {showSettings && (
          <Card className="border-indigo-200 shadow-md">
            <CardHeader className="pb-2 bg-indigo-50 rounded-t-lg">
              <CardTitle className="text-base flex items-center gap-2 text-indigo-900">
                <Play className="h-4 w-4" />
                Run Verification
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3 pt-4">
              {/* Next Puzzle */}
              {nextPuzzle && (
                <div className="flex items-center gap-2 bg-white border border-green-200 rounded p-2 text-sm shadow-sm">
                  <Target className="h-4 w-4 text-green-600" />
                  <span className="font-medium">Recommended Next Puzzle:</span>
                  <Badge variant="outline" className="font-mono text-base">{nextPuzzle}</Badge>
                </div>
              )}

              {/* Settings Grid */}
              <div className="grid md:grid-cols-3 gap-4">
                <div className="space-y-1">
                  <Label className="text-xs font-semibold text-gray-500">Model Configuration</Label>
                  <Select
                    value={selectedModelId}
                    onValueChange={setSelectedModelId}
                    disabled={modelsLoading || !selectedModel}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {models.length === 0 ? (
                        <SelectItem value="loading" disabled>
                          Loading models...
                        </SelectItem>
                      ) : (
                        models.map(m => (
                          <SelectItem key={m.id} value={m.id}>
                            {`${m.name} · ${m.provider} · ${getRoutingLabel(m.routing)}`}
                          </SelectItem>
                        ))
                      )}
                    </SelectContent>
                  </Select>
                  <p className="text-[10px] text-gray-500 font-mono">
                    {selectedModel ? selectedModel.id : 'Model metadata will appear once loaded.'}
                  </p>
                  <div className="flex flex-wrap gap-2">
                    {selectedModel ? (
                      <>
                        <Badge variant="outline">{selectedModel.provider}</Badge>
                        <Badge variant="secondary">{routingLabel}</Badge>
                        {(globalByokRequired || selectedModel.requiresBYO) && (
                          <Badge variant="destructive">
                            {globalByokRequired ? 'API Key Required (Production)' : 'BYO Key Required'}
                          </Badge>
                        )}
                      </>
                    ) : (
                      <Badge variant="outline">Loading model info…</Badge>
                    )}
                  </div>
                </div>

                <div className="space-y-1">
                  <Label className="text-xs font-semibold text-gray-500">Expert Count</Label>
                  <Select value={numExperts} onValueChange={setNumExperts}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {EXPERT_OPTIONS.map(opt => (
                        <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-1">
                  <Label className="text-xs font-semibold text-gray-500">API Key {requiresApiKey ? '(Required)' : '(Optional)'}</Label>
                  <Input
                    type="text"
                    placeholder={keyPlaceholder}
                    value={apiKey}
                    onChange={(e) => setApiKey(e.target.value)}
                    className="font-mono text-sm"
                  />
                </div>
              </div>

              {/* Info */}
              <div className="text-xs text-gray-500">
                {requiresApiKey ? (
                  usingProjectKey ? (
                    <span>
                      This model requires your own API key{selectedModel?.provider ? ` for ${selectedModel.provider}` : ''}.
                      {' '}Provide a key above to run the solver.
                    </span>
                  ) : (
                    <span>Using your key — passed directly to Python backend, never stored.</span>
                  )
                ) : (
                  usingProjectKey ? (
                    <span>
                      Using project key (may be rate limited).
                      {' '}<a href={providerKeyUrl} target="_blank" rel="noreferrer" className="text-indigo-600 underline">Get your own</a> for faster results.
                    </span>
                  ) : (
                    <span>Using your key — passed directly to Python backend, never stored.</span>
                  )
                )}
              </div>

              {/* Run Button */}
              <Button
                onClick={handleRunNext}
                disabled={!canStart || (requiresApiKey && usingProjectKey)}
                className="w-full bg-green-600 hover:bg-green-700 h-10 text-base font-semibold"
              >
                <Play className="h-4 w-4 mr-2" />
                Run Solver on {nextPuzzle || 'Next Puzzle'}
              </Button>
            </CardContent>
          </Card>
        )}

        {/* Definitions Section */}
        <Card className="bg-amber-50 border-amber-200">
          <CardHeader className="pb-3">
            <CardTitle className="text-lg flex items-center gap-2 text-amber-900">
              <AlertCircle className="h-5 w-5" />
              Technical Definitions
            </CardTitle>
            <CardDescription className="text-amber-800">
              We are independent community auditors providing a visual explainer for Poetiq's claims. Here are the key technical terms:
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <h3 className="font-semibold text-amber-900 mb-1">Pareto Frontier (Pareto Optimal)</h3>
              <p className="text-sm text-gray-700">
                In optimization, a <strong>Pareto frontier</strong> is the set of solutions where improving one metric (e.g., accuracy)
                necessarily worsens another (e.g., cost). A solution is Pareto optimal if no other solution can improve one dimension
                without degrading another. In this context, Poetiq claims their configurations achieve better accuracy-to-cost ratios
                than existing systems across multiple operating points.
              </p>
            </div>
            <div>
              <h3 className="font-semibold text-amber-900 mb-1">Recursive Self-Improving Meta-System</h3>
              <p className="text-sm text-gray-700">
                A <strong>meta-system</strong> is a system that operates on other systems. "Recursive self-improving" means the system
                can analyze its own outputs, identify failures, and generate improved versions iteratively. In Poetiq's case, the
                meta-system orchestrates multiple LLM calls to generate code, test it, analyze failures, and refine—repeating until
                convergence. "LLM-agnostic" means the architecture can work with different underlying models (Gemini, GPT, Grok, etc.)
                without fundamental redesign.
              </p>
            </div>
            <div className="bg-white border border-amber-200 rounded p-3">
              <p className="text-xs text-amber-900 font-medium">
                <strong>Our Role:</strong> We are not affiliated with Poetiq. We are independent community members auditing their
                claims by running their open-source solver on ARC-AGI puzzles and documenting the results. This page serves as
                a visual explainer and data collection tool.
              </p>
            </div>
          </CardContent>
        </Card>

        {/* Deep Dive Section - Merged from PoetiqExplainer */}
        <div className="grid lg:grid-cols-2 gap-8">
          
          {/* Left Col: The Meta-System */}
          <div className="space-y-6">
            <div>
              <h2 className="text-xl font-bold flex items-center gap-2 text-indigo-900 mb-2">
                <Brain className="h-6 w-6" />
                The Poetiq Meta-System
              </h2>
              <p className="text-gray-700 leading-relaxed">
                According to their documentation, Poetiq implements a <strong>recursive, self-improving meta-system</strong>.
                Rather than single-prompt prediction, the system orchestrates multiple LLM calls to generate code, test it against
                training examples, and iteratively refine based on execution feedback.
              </p>
            </div>

            <div className="bg-white p-4 rounded-lg border border-indigo-100 shadow-sm space-y-4">
               <h3 className="font-semibold text-indigo-800 flex items-center gap-2">
                 <RefreshCw className="h-4 w-4" />
                 Self-Auditing Loop
               </h3>
               <div className="space-y-3">
                  <ProcessStep number={1} icon={Brain} title="Analyze" description="AI studies input/output pairs to hypothesize a pattern." />
                  <ProcessStep number={2} icon={Code} title="Generate Code" description="Writes a Python transform() function to implement the logic." />
                  <ProcessStep number={3} icon={TestTube} title="Test & Audit" description="Runs code on training examples. Self-audits: 'Did it work?'" />
                  <ProcessStep number={4} icon={TrendingUp} title="Iterate" description="If failed, analyzes stderr/results and refines the code." />
                  <ProcessStep number={5} icon={CheckCircle} title="Convergence" description="Only submits when code passes all internal checks." />
               </div>
            </div>
          </div>

          {/* Right Col: Pareto & Models */}
          <div className="space-y-6">
            
            {/* Measured Results */}
            <div className="space-y-2">
               <h2 className="text-xl font-bold flex items-center gap-2 text-green-800 mb-2">
                  <TrendingUp className="h-6 w-6" />
                  Measured Results
               </h2>
               <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                  <p className="text-sm text-green-900 mb-3">
                     Community audit results from independent testing. Poetiq claims their system establishes a Pareto frontier
                     with better accuracy-to-cost ratios than prior systems.
                  </p>
                  <div className="grid grid-cols-2 gap-3">
                     <div className="bg-white p-3 rounded border border-green-100">
                        <div className="text-xs text-gray-500 uppercase font-bold">Cost / Solve</div>
                        <div className="text-lg font-bold text-green-700">
                          {progress.avgCostPerSolve ? `$${progress.avgCostPerSolve.toFixed(3)}` : 'No data'}
                        </div>
                        <div className="text-xs text-gray-600">measured average</div>
                     </div>
                     <div className="bg-white p-3 rounded border border-green-100">
                        <div className="text-xs text-gray-500 uppercase font-bold">Success Rate</div>
                        <div className="text-lg font-bold text-green-700">
                          {progress.attempted > 0 ? `${progress.successRateOnAttempted}%` : 'No data'}
                        </div>
                        <div className="text-xs text-gray-600">on attempted ({progress.attempted} tests)</div>
                     </div>
                  </div>
               </div>
            </div>

            {/* LLM Agnostic */}
            <div className="space-y-2">
               <h2 className="text-xl font-bold flex items-center gap-2 text-purple-800 mb-2">
                  <Cpu className="h-6 w-6" />
                  LLM-Agnostic Architecture
               </h2>
               <p className="text-gray-700 text-sm mb-3">
                  Poetiq's architecture is designed to work with multiple LLM providers. Their blog post claims the system
                  was adapted on open-source models and then applied to various commercial models. We are testing the following
                  configurations:
               </p>
               <div className="grid gap-2">
                  <ModelCard
                     name="Gemini 3"
                     variant="Pro Preview"
                     role="Latest Google model used in reported SOTA configurations."
                     color="bg-blue-50 border-blue-200 text-blue-800"
                  />
                  <ModelCard
                     name="GPT-5.1"
                     variant=""
                     role="OpenAI model used in mixed-model configurations."
                     color="bg-gray-100 border-gray-200 text-gray-800"
                  />
               </div>
            </div>
          </div>

        </div>

        <Separator className="my-8" />

        {/* Main Content: Progress Metrics */}
        <div className="space-y-4">
           <div>
              <h2 className="text-lg font-semibold text-gray-800 mb-3">Test Coverage & Performance</h2>
              <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                {/* Attempted Coverage */}
                <Card>
                  <CardContent className="pt-6">
                    <div className="text-xs text-gray-500 font-semibold uppercase mb-1">Coverage</div>
                    <div className="text-2xl font-bold text-blue-600">{progress.attemptedPercentage}%</div>
                    <div className="text-xs text-gray-600">{progress.attempted}/{progress.total} tested</div>
                  </CardContent>
                </Card>

                {/* Success Rate on Attempted */}
                <Card>
                  <CardContent className="pt-6">
                    <div className="text-xs text-gray-500 font-semibold uppercase mb-1">Success Rate</div>
                    <div className="text-2xl font-bold text-green-600">{progress.successRateOnAttempted}%</div>
                    <div className="text-xs text-gray-600">{progress.solved}/{progress.attempted} solved</div>
                  </CardContent>
                </Card>

                {/* Total Cost */}
                <Card>
                  <CardContent className="pt-6">
                    <div className="text-xs text-gray-500 font-semibold uppercase mb-1">Total Cost</div>
                    <div className="text-2xl font-bold text-purple-600">${progress.totalCost.toFixed(2)}</div>
                    <div className="text-xs text-gray-600">{progress.attempted} attempts</div>
                  </CardContent>
                </Card>

                {/* Avg Cost per Solve */}
                <Card>
                  <CardContent className="pt-6">
                    <div className="text-xs text-gray-500 font-semibold uppercase mb-1">Cost / Solve</div>
                    <div className="text-2xl font-bold text-emerald-600">{progress.avgCostPerSolve ? `$${progress.avgCostPerSolve.toFixed(3)}` : '—'}</div>
                    <div className="text-xs text-gray-600">per success</div>
                  </CardContent>
                </Card>

                {/* Avg Cost per Attempt */}
                <Card>
                  <CardContent className="pt-6">
                    <div className="text-xs text-gray-500 font-semibold uppercase mb-1">Cost / Attempt</div>
                    <div className="text-2xl font-bold text-orange-600">{progress.avgCostPerAttempt ? `$${progress.avgCostPerAttempt.toFixed(3)}` : '—'}</div>
                    <div className="text-xs text-gray-600">all attempts</div>
                  </CardContent>
                </Card>
              </div>
           </div>

           {/* Iteration Efficiency */}
           {(progress.avgIterationsSolved !== null || progress.avgIterationsFailed !== null) && (
             <div>
               <h3 className="text-base font-semibold text-gray-800 mb-2">Iteration Efficiency</h3>
               <div className="grid grid-cols-2 gap-3">
                 <Card>
                   <CardContent className="pt-6">
                     <div className="text-xs text-gray-500 font-semibold uppercase mb-1">Avg Iterations (Solved)</div>
                     <div className="text-2xl font-bold text-emerald-600">{progress.avgIterationsSolved ?? '—'}</div>
                     <div className="text-xs text-gray-600">iterations to solve</div>
                   </CardContent>
                 </Card>
                 <Card>
                   <CardContent className="pt-6">
                     <div className="text-xs text-gray-500 font-semibold uppercase mb-1">Avg Iterations (Failed)</div>
                     <div className="text-2xl font-bold text-orange-600">{progress.avgIterationsFailed ?? '—'}</div>
                     <div className="text-xs text-gray-600">iterations before giving up</div>
                   </CardContent>
                 </Card>
               </div>
             </div>
           )}

           {/* Per-Model Breakdown */}
           {progress.modelStats.length > 0 && (
             <div>
               <h3 className="text-base font-semibold text-gray-800 mb-2">Model Performance</h3>
               <Card>
                 <CardContent className="pt-4">
                   <div className="space-y-2">
                     {progress.modelStats.map((stat) => (
                       <div key={stat.modelName} className="flex items-center justify-between py-2 px-3 bg-gray-50 rounded text-sm">
                         <div className="font-mono text-gray-700 flex-1">{stat.modelName}</div>
                         <div className="flex items-center gap-4">
                           <div className="text-gray-600">{stat.solved}/{stat.attempts} solved</div>
                           <div className="font-bold text-gray-800 min-w-12 text-right">{stat.successRate}%</div>
                         </div>
                       </div>
                     ))}
                   </div>
                 </CardContent>
               </Card>
             </div>
           )}

           {/* Puzzle Grid */}
           <div>
              <h3 className="text-base font-semibold text-gray-800 mb-2">Puzzle Status</h3>
              <PuzzleProgressGrid
                puzzles={progress.puzzles}
                isLoading={progress.isLoading}
              />
           </div>
        </div>

        {/* Footer Links */}
        <div className="pt-8 border-t text-center text-sm text-gray-500 space-x-4">
          <a href="https://poetiq.ai/posts/arcagi_announcement/" target="_blank" className="text-indigo-600 hover:underline font-medium">
            Original Blog Post
          </a>
          <span>·</span>
          <a href="https://github.com/82deutschmark/poetiq-arc-agi-solver" target="_blank" className="text-indigo-600 hover:underline font-medium">
            Source Code (GitHub)
          </a>
          <span>·</span>
          <a href="https://arcprize.org/" target="_blank" className="text-indigo-600 hover:underline font-medium">
            ARC Prize
          </a>
        </div>
      </div>
    </div>
  );
}

// Helper components
function ProcessStep({ number, icon: Icon, title, description }: any) {
  return (
    <div className="flex items-start gap-3">
      <div className="flex items-center justify-center w-6 h-6 rounded-full bg-indigo-100 text-indigo-600 font-bold text-xs shrink-0 mt-0.5">
        {number}
      </div>
      <div className="min-w-0">
        <div className="flex items-center gap-1.5 font-medium text-gray-900 text-sm">
          <Icon className="h-3.5 w-3.5 text-indigo-600" />
          {title}
        </div>
        <p className="text-xs text-gray-600 leading-snug">{description}</p>
      </div>
    </div>
  );
}

function ModelCard({ name, variant, role, color }: any) {
   return (
      <div className={`p-2 rounded border flex items-center justify-between ${color}`}>
         <div>
            <div className="font-bold text-sm">{name} <span className="opacity-75 font-normal">({variant})</span></div>
            <div className="text-xs opacity-90">{role}</div>
         </div>
      </div>
   );
}
